/**
 * ============================================================
 * ESENCIA — SELECCIÓN ESPECIAL VISUAL GLOBAL
 * V2.0
 * ============================================================
 *
 * ÚNICA REGLA:
 *
 * Producto con tag:
 *   seleccion-especial-70
 *
 * muestra:
 *   Selección especial +$70
 *
 * Funciona globalmente:
 * - Inicio
 * - Arma tu Esencia
 * - Makeup
 * - Skin Care
 * - Colecciones futuras
 * - Recomendaciones
 * - Búsquedas/listados
 *
 * NO controla el cobro.
 * El cobro real pertenece a:
 * esencia-special-extra.js
 * ============================================================
 */

(() => {

  'use strict';

  /*
   * El carrito ya tiene su propio diseño de
   * Selección especial en cart-products.liquid.
   * El sistema visual global NO debe intervenir aquí.
   */
  const currentPath = window.location.pathname.replace(/\/+$/, '');

  if (
    currentPath === '/cart' ||
    currentPath.endsWith('/cart')
  ) {
    return;
  }


  /* ============================================================
     EVITAR DOBLE CARGA
     ============================================================ */

  if (
    window.__ESENCIA_SPECIAL_VISUAL_V2__
  ) {
    return;
  }

  window.__ESENCIA_SPECIAL_VISUAL_V2__ =
    true;



  /* ============================================================
     CONFIGURACIÓN
     ============================================================ */

  const CONFIG = {

    specialTag:
      'seleccion-especial-70',

    helperHandle:
      'extra-por-seleccion-especial',

    amount:
      70

  };


  const PRODUCT_CACHE =
    new Map();


  let scanTimer =
    null;



  /* ============================================================
     UTILIDADES
     ============================================================ */

  function normalize(value) {

    return String(
      value || ''
    )
      .trim()
      .toLowerCase();

  }


  function getRoot() {

    const root =
      window.Shopify?.routes?.root ||
      '/';


    return root.endsWith('/')
      ? root
      : `${root}/`;

  }


  function getHandleFromHref(href) {

    if (!href) {
      return '';
    }


    try {

      const url =
        new URL(
          href,
          window.location.origin
        );


      const match =
        url.pathname.match(
          /\/products\/([^/?#]+)/
        );


      if (
        !match ||
        !match[1]
      ) {
        return '';
      }


      return decodeURIComponent(
        match[1]
      );


    } catch (error) {

      return '';

    }

  }


  function normalizeTags(tags) {

    if (
      Array.isArray(tags)
    ) {

      return tags
        .map(normalize)
        .filter(Boolean);

    }


    if (
      typeof tags === 'string'
    ) {

      return tags
        .split(',')
        .map(normalize)
        .filter(Boolean);

    }


    return [];

  }



  /* ============================================================
     OBTENER PRODUCTO DESDE SHOPIFY
     ============================================================ */

  async function getProduct(handle) {

    if (!handle) {
      return null;
    }


    if (
      normalize(handle) ===
      normalize(CONFIG.helperHandle)
    ) {
      return null;
    }


    if (
      PRODUCT_CACHE.has(handle)
    ) {

      return PRODUCT_CACHE.get(
        handle
      );

    }


    const request =
      fetch(
        `${getRoot()}products/${encodeURIComponent(handle)}.js`,
        {

          credentials:
            'same-origin',

          headers: {

            Accept:
              'application/json'

          }

        }
      )
        .then(response => {

          if (!response.ok) {

            throw new Error(
              `HTTP ${response.status}`
            );

          }


          return response.json();

        })
        .catch(error => {

          PRODUCT_CACHE.delete(
            handle
          );


          console.warn(
            '[Esencia Visual] No se pudo leer:',
            handle,
            error
          );


          return null;

        });


    PRODUCT_CACHE.set(
      handle,
      request
    );


    return request;

  }



  /* ============================================================
     SABER SI ES ESPECIAL
     ============================================================ */

  async function isSpecialProduct(
    handle
  ) {

    const product =
      await getProduct(
        handle
      );


    if (!product) {
      return false;
    }


    const tags =
      normalizeTags(
        product.tags
      );


    return tags.includes(
      normalize(
        CONFIG.specialTag
      )
    );

  }



  /* ============================================================
     ENCONTRAR TODOS LOS HANDLES DENTRO DE UN ELEMENTO
     ============================================================ */

  function getHandlesInside(
    element
  ) {

    const handles =
      new Set();


    if (
      !element ||
      !element.querySelectorAll
    ) {

      return handles;

    }


    element
      .querySelectorAll(
        'a[href*="/products/"]'
      )
      .forEach(link => {

        const handle =
          getHandleFromHref(
            link.getAttribute(
              'href'
            )
          );


        if (
          handle &&
          normalize(handle) !==
          normalize(
            CONFIG.helperHandle
          )
        ) {

          handles.add(
            handle
          );

        }

      });


    return handles;

  }



  /* ============================================================
     ENCONTRAR TARJETA COMPLETA
     ============================================================ */

  function findProductCard(
    link,
    handle
  ) {

    if (!link) {
      return null;
    }


    /*
     * IMPORTANTE:
     *
     * Primero buscamos los contenedores GRANDES.
     *
     * La versión anterior encontraba .card demasiado pronto.
     * En Makeup ese .card era prácticamente solo la imagen,
     * mientras precio e información estaban fuera.
     */

    const preferredSelectors = [

      '.grid__item',

      '.product-grid__item',

      '[data-product-grid-item]',

      'li[data-product-id]',

      'li',

      'article',

      '[data-product-card]',

      'product-card',

      '.product-card-wrapper',

      '.card-wrapper',

      '[class*="product-card"]'

    ];


    for (
      const selector
      of preferredSelectors
    ) {

      const candidate =
        link.closest(
          selector
        );


      if (!candidate) {
        continue;
      }


      const handles =
        getHandlesInside(
          candidate
        );


      /*
       * La tarjeta debe contener solamente
       * este producto.
       */

      if (
        handles.size === 1 &&
        handles.has(handle)
      ) {

        return candidate;

      }

    }



    /*
     * FALLBACK INTELIGENTE
     *
     * Vamos subiendo por el DOM.
     * Mientras el contenedor siga teniendo
     * solamente un producto, lo consideramos
     * parte de esa tarjeta.
     */

    let node =
      link;


    let best =
      null;


    let depth =
      0;


    while (
      node &&
      node !== document.body &&
      depth < 10
    ) {

      if (
        node.nodeType === 1
      ) {

        const handles =
          getHandlesInside(
            node
          );


        if (
          handles.size === 1 &&
          handles.has(handle)
        ) {

          const text =
            normalize(
              node.textContent
            );


          const hasPrice =
            /\$\s*[\d,.]+/.test(
              text
            );


          if (
            hasPrice ||
            depth >= 2
          ) {

            best =
              node;

          }

        }


        if (
          handles.size > 1
        ) {

          break;

        }

      }


      node =
        node.parentElement;


      depth += 1;

    }


    return (
      best ||
      link.parentElement
    );

  }



  /* ============================================================
     VER SI YA EXISTE EL BADGE
     ============================================================ */

  function hasSpecialBadge(
    card
  ) {

    if (!card) {
      return true;
    }


    if (
      card.querySelector(
        '[data-esencia-special-visual]'
      )
    ) {

      return true;

    }


    /*
     * Home personalizado.
     */

    if (
      card.querySelector(
        '.esencia-product-card__special'
      )
    ) {

      return true;

    }


    /*
     * Cualquier badge creado por otro
     * Liquid de Esencia.
     */

    const elements =
      card.querySelectorAll(
        'span, div, p, small'
      );


    for (
      const element
      of elements
    ) {

      const text =
        normalize(
          element.textContent
        );


      if (
        text ===
          'selección especial +$70' ||

        text ===
          'seleccion especial +$70'
      ) {

        return true;

      }

    }


    return false;

  }



  /* ============================================================
     CREAR BADGE
     ============================================================ */

  function createBadge() {

    const badge =
      document.createElement(
        'div'
      );


    badge.className =
      'esencia-special-global-badge';


    badge.setAttribute(
      'data-esencia-special-visual',
      'true'
    );


    badge.textContent =
      `Selección especial +$${CONFIG.amount}`;


    return badge;

  }



  /* ============================================================
     BUSCAR EL BLOQUE DE PRECIO
     ============================================================ */

  function findPriceBlock(
    card
  ) {

    if (!card) {
      return null;
    }


    /*
     * Primero nuestros elementos conocidos.
     */

    const knownSelectors = [

      '.nova-box-card-offer',

      '[data-esencia-card-price]',

      '[data-esencia-price]',

      '.esencia-product-card__price-row',

      '.esencia-product-card__price',

      '.price',

      '.card__price',

      '.price__container'

    ];


    for (
      const selector
      of knownSelectors
    ) {

      const elements =
        card.querySelectorAll(
          selector
        );


      if (
        elements.length
      ) {

        return elements[
          elements.length - 1
        ];

      }

    }



    /*
     * Makeup / Skin Care y temas dinámicos:
     *
     * buscamos visualmente el elemento
     * que contiene un precio.
     */

    const candidates =
      Array.from(
        card.querySelectorAll(
          'div, p, span, strong, small'
        )
      )
        .filter(element => {

          const text =
            String(
              element.textContent ||
              ''
            )
              .replace(
                /\s+/g,
                ' '
              )
              .trim();


          if (
            text.length < 2 ||
            text.length > 100
          ) {

            return false;

          }


          return (
            /\$\s*[\d,.]+/.test(
              text
            )
          );

        });


    if (
      !candidates.length
    ) {

      return null;

    }


    /*
     * Preferimos precio que además diga c/u,
     * pieza, Esencia, desde...
     */

    const preferred =
      candidates.filter(
        element => {

          const text =
            normalize(
              element.textContent
            );


          return (
            text.includes('c/u') ||
            text.includes('pieza') ||
            text.includes('esencia') ||
            text.includes('desde')
          );

        }
      );


    const selected =
      preferred.length

        ? preferred[
            preferred.length - 1
          ]

        : candidates[
            candidates.length - 1
          ];


    /*
     * Si seleccionamos únicamente <strong>$300</strong>,
     * subimos al pequeño contenedor del precio.
     */

    let target =
      selected;


    for (
      let i = 0;
      i < 3;
      i += 1
    ) {

      const parent =
        target.parentElement;


      if (
        !parent ||
        parent === card
      ) {

        break;

      }


      const text =
        String(
          parent.textContent ||
          ''
        )
          .replace(
            /\s+/g,
            ' '
          )
          .trim();


      if (
        text.length <= 120 &&
        /\$\s*[\d,.]+/.test(
          text
        )
      ) {

        target =
          parent;

      } else {

        break;

      }

    }


    return target;

  }



  /* ============================================================
     ENCONTRAR BLOQUE DE INFORMACIÓN
     ============================================================ */

  function findInformationBlock(
    card
  ) {

    if (!card) {
      return null;
    }


    return (

      card.querySelector(
        '.esencia-product-card__content'
      ) ||

      card.querySelector(
        '.card__information'
      ) ||

      card.querySelector(
        '.card-information'
      ) ||

      card.querySelector(
        '[class*="product-info"]'
      ) ||

      card

    );

  }



  /* ============================================================
     INSERTAR BADGE
     ============================================================ */

  function renderSpecialBadge(
    card
  ) {

    if (
      !card ||
      hasSpecialBadge(card)
    ) {

      return;

    }


    const badge =
      createBadge();


    const priceBlock =
      findPriceBlock(
        card
      );


    if (
      priceBlock &&
      priceBlock.parentNode
    ) {

      priceBlock.insertAdjacentElement(
        'afterend',
        badge
      );


      return;

    }


    const information =
      findInformationBlock(
        card
      );


    if (information) {

      information.appendChild(
        badge
      );

    }

  }



  /* ============================================================
     PROCESAR LINK DE PRODUCTO
     ============================================================ */

  async function processProductLink(
    link
  ) {

    if (
      !link ||
      !link.isConnected
    ) {

      return;

    }


    const handle =
      getHandleFromHref(
        link.getAttribute(
          'href'
        )
      );


    if (!handle) {
      return;
    }


    if (
      normalize(handle) ===
      normalize(
        CONFIG.helperHandle
      )
    ) {

      return;

    }


    const card =
      findProductCard(
        link,
        handle
      );


    if (!card) {
      return;
    }


    /*
     * Si ya está visualmente correcto,
     * terminamos aquí.
     */

    if (
      hasSpecialBadge(
        card
      )
    ) {

      return;

    }


    const special =
      await isSpecialProduct(
        handle
      );


    if (!special) {
      return;
    }


    /*
     * La tarjeta pudo ser sustituida mientras
     * Shopify respondía.
     */

    if (
      !card.isConnected
    ) {

      scheduleScan(
        50
      );


      return;

    }


    renderSpecialBadge(
      card
    );

  }



  /* ============================================================
     ESCANEAR TODA LA PÁGINA
     ============================================================ */

  function scan(
    root = document
  ) {

    if (
      !root ||
      !root.querySelectorAll
    ) {

      return;

    }


    const links =
      Array.from(
        root.querySelectorAll(
          'a[href*="/products/"]'
        )
      );


    const processed =
      new Set();


    links.forEach(
      link => {

        const handle =
          getHandleFromHref(
            link.getAttribute(
              'href'
            )
          );


        if (!handle) {
          return;
        }


        /*
         * Evita procesar imagen y título
         * de la misma tarjeta dos veces
         * en el mismo escaneo.
         */

        const card =
          findProductCard(
            link,
            handle
          );


        if (!card) {
          return;
        }


        if (
          processed.has(
            card
          )
        ) {

          return;

        }


        processed.add(
          card
        );


        processProductLink(
          link
        );

      }
    );

  }



  /* ============================================================
     PROGRAMAR ESCANEO
     ============================================================ */

  function scheduleScan(
    delay = 80
  ) {

    if (
      scanTimer
    ) {

      clearTimeout(
        scanTimer
      );

    }


    scanTimer =
      setTimeout(
        () => {

          scanTimer =
            null;


          scan(
            document
          );

        },
        delay
      );

  }



  /* ============================================================
     OBSERVAR CAMBIOS
     ============================================================ */

  const observer =
    new MutationObserver(
      mutations => {

        let relevant =
          false;


        for (
          const mutation
          of mutations
        ) {

          if (
            mutation.addedNodes &&
            mutation.addedNodes.length
          ) {

            relevant =
              true;

            break;

          }

        }


        if (relevant) {

          /*
           * Makeup/Skin Care pueden modificar
           * las tarjetas DESPUÉS del primer render.
           */

          scheduleScan(
            120
          );

        }

      }
    );



  /* ============================================================
     ESTILOS
     ============================================================ */

  function injectStyles() {

    if (
      document.getElementById(
        'EsenciaSpecialVisualV2Styles'
      )
    ) {

      return;

    }


    const style =
      document.createElement(
        'style'
      );


    style.id =
      'EsenciaSpecialVisualV2Styles';


    style.textContent = `

      .esencia-special-global-badge {

        display: flex !important;

        width: max-content !important;

        max-width: 100% !important;

        min-height: 24px !important;

        align-items: center !important;

        justify-content: center !important;

        margin:
          7px 0 0 !important;

        padding:
          5px 10px !important;

        border:
          1px solid
          rgba(232,121,233,.34) !important;

        border-radius:
          999px !important;

        background:
          rgba(255,246,255,.96) !important;

        color:
          #8a4b88 !important;

        font-family:
          var(
            --esencia-font-body,
            'DM Sans',
            sans-serif
          ) !important;

        font-size:
          8px !important;

        font-weight:
          750 !important;

        line-height:
          1.15 !important;

        letter-spacing:
          0 !important;

        text-transform:
          none !important;

        white-space:
          nowrap !important;

        opacity:
          1 !important;

        visibility:
          visible !important;

        position:
          relative !important;

        z-index:
          3 !important;

        pointer-events:
          none !important;

      }


      @media screen and (max-width: 749px) {

        .esencia-special-global-badge {

          min-height:
            22px !important;

          margin-top:
            6px !important;

          padding:
            4px 8px !important;

          font-size:
            7px !important;

        }

      }

    `;


    document.head.appendChild(
      style
    );

  }



  /* ============================================================
     INICIO
     ============================================================ */

  function init() {

    injectStyles();


    scan(
      document
    );


    if (
      document.body
    ) {

      observer.observe(
        document.body,
        {

          childList:
            true,

          subtree:
            true

        }
      );

    }


    /*
     * Algunos scripts de Esencia y Shopify
     * terminan de modificar las tarjetas después.
     */

    [
      300,
      800,
      1500,
      2500
    ].forEach(
      delay => {

        setTimeout(
          () => {

            scan(
              document
            );

          },
          delay
        );

      }
    );

  }



  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once:
          true
      }
    );

  } else {

    init();

  }



  /* ============================================================
     EVENTOS
     ============================================================ */

  document.addEventListener(
    'shopify:section:load',
    () => {

      scheduleScan(
        50
      );

    }
  );


  document.addEventListener(
    'esencia:cart-changed',
    () => {

      scheduleScan(
        80
      );

    }
  );


  window.addEventListener(
    'pageshow',
    () => {

      scheduleScan(
        80
      );

    }
  );



  /* ============================================================
     API
     ============================================================ */

  window.EsenciaSpecialVisual = {

    refresh() {

      PRODUCT_CACHE.clear();


      scheduleScan(
        0
      );

    },


    clearCache() {

      PRODUCT_CACHE.clear();

    }

  };

})();